import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Scale,
  FileText,
  CheckCircle,
  Clock
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import legalTermsMasterService from '../../services/legalTermsMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const LegalTermsMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTemplates: 0,
    activeTemplates: 0,
    inactiveTemplates: 0,
    draftTemplates: 0,
    totalUsage: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [termsResponse, statsResponse] = await Promise.all([
        legalTermsMasterService.getAllLegalTerms(),
        legalTermsMasterService.getLegalTermsStats()
      ])
      
      if (termsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (termsResponse.data || []).map(term => ({
          ...term,
          id: term._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (termsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No legal terms template records found. Create your first template!')
        }
      } else {
        toast.error(termsResponse.message || 'Failed to load legal terms')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading legal terms master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view legal terms')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load legal terms master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getCategoryBadgeClass = (category) => {
    const categoryMap = {
      'General Terms': 'active',
      'Payment Terms': 'pending',
      'Delivery Terms': 'pending',
      'Warranty Terms': 'warning',
      'Liability Terms': 'danger',
      'Confidentiality': 'active',
      'Intellectual Property': 'warning',
      'Termination': 'inactive',
      'Dispute Resolution': 'pending',
      'Other': 'inactive'
    }
    return categoryMap[category] || 'inactive'
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Active': 'active',
      'Inactive': 'inactive',
      'Draft': 'pending',
      'Archived': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'legalTermsCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'legalTermsName',
      header: 'Template Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getCategoryBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'version',
      header: 'Version',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {getValue() || '1.0'}
        </div>
      ),
    },
    {
      accessorKey: 'usageCount',
      header: 'Usage',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {getValue() || 0}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/contracts/legal-terms-master/form')
  }

  const handleEdit = (termData) => {
    navigate('/contracts/legal-terms-master/edit', { state: { termData } })
  }

  const handleView = (termData) => {
    navigate('/contracts/legal-terms-master/view', { state: { termData } })
  }

  const handleDelete = (termData) => {
    setDeleteItem(termData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting legal terms template...')
      const response = await legalTermsMasterService.deleteLegalTerms(deleteItem._id)
      if (response.success) {
        toast.success('Legal terms template deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete legal terms template')
      }
    } catch (error) {
      console.error('Error deleting legal terms template:', error)
      if (error.response?.status === 404) {
        toast.error('Legal terms template not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this legal terms template')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete legal terms template. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Contracts', path: '/contracts' },
          { label: 'Legal Terms', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Legal Terms</h1>
          <p>Standard legal terms and conditions templates</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Template
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Scale size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalTemplates || 0}</div>
          <div className="metric-title">Total Templates</div>
          <div className="metric-subtitle">All legal templates</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeTemplates || 0}</div>
          <div className="metric-title">Active Templates</div>
          <div className="metric-subtitle">In use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftTemplates || 0}</div>
          <div className="metric-title">Draft Templates</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Usage</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalUsage || 0}</div>
          <div className="metric-title">Total Usage</div>
          <div className="metric-subtitle">Times applied</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Legal Terms Template"
        message={`Are you sure you want to delete legal terms template "${deleteItem?.legalTermsName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default LegalTermsMaster

